import React, { useEffect, useState } from 'react';

/**
 * HeroVideoMontage
 * ----------------
 * Premium cinematic hero background. Pure-CSS animation — no JS driver,
 * no video decoding, no setTimeout scheduler. Each slide is a layered image
 * with its own slow Ken Burns zoom/pan and an opacity window staggered
 * around the loop, so the viewer only ever sees one soft crossfade in
 * progress. Entirely compositor-thread (opacity + transform + translate3d),
 * which gives a consistent frame rate on both desktop and mobile.
 *
 * Images are genuine Visual Creationz hero stills (poster + The Lake at
 * Barston project frames). No new generated/stock imagery.
 */

// Ordered slide list. One slot = 8s. Loop = SLIDES.length * 8s.
// All slides are full-bleed cinematic frames (poster + Lake at Barston project + Wedding Reception sample).
const SLIDES = [
  'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=2200&q=85',
  '/hero/lake-aerial.jpg',
  '/hero/lake-hands.jpg',
  '/hero/lake-cheers.jpg',
  '/hero/wedding-quote.jpg',
];

const SLIDE_SECONDS = 8;
const CYCLE_SECONDS = SLIDES.length * SLIDE_SECONDS;

const HeroVideoMontage = () => {
  const [animate, setAnimate] = useState(true);

  // Respect prefers-reduced-motion — show the first slide statically.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setAnimate(!mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      data-testid="hero-video-montage"
      aria-hidden="true"
    >
      {/* Hero slide stack - pure CSS, GPU-accelerated */}
      <div className="absolute inset-0">
        {SLIDES.map((src, i) => (
          <div
            key={src}
            className="hero-slide"
            style={{
              backgroundImage: `url(${src})`,
              animationDelay: animate ? `${i * SLIDE_SECONDS}s` : '0s',
              animationDuration: animate ? `${CYCLE_SECONDS}s` : '0s',
              // The very first slide needs to be visible on first paint
              // (before its animation delay elapses) to avoid a black flash.
              opacity: animate ? undefined : (i === 0 ? 1 : 0),
            }}
          />
        ))}
      </div>

      {/* Preload the remaining slides so they are decoded before their first turn.
          Hidden from layout; the browser still fetches these img tags. */}
      <div className="sr-only" aria-hidden="true">
        {SLIDES.map((src) => (
          <img key={src} src={src} alt="" loading="eager" fetchPriority="low" />
        ))}
      </div>

      {/* Grading + overlays preserved from the previous implementation
          so copy and buttons remain crisp against the imagery. */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50" />
      <div
        className="absolute inset-0 opacity-[0.07] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
    </div>
  );
};

export default HeroVideoMontage;
